//
// SchemaWorkspace
//
// Manage PIM Definitions, Classes, Fields, and Layouts, we use a navigator/
// worksheet/inspector setup.

import { observer } from 'mobx-react-lite'
import { useRef } from 'react'
import { useDrop } from 'react-dnd'
import { usePersistentState } from '../hooks/usePersistentState'
import { useStore } from '../stores'

import { DragTypes } from '../dragdrop/DragTypes'

import { Divider, HView, Spacer, VScrollView, VView } from '../appview'
import { Button, Icon } from '../components'
import { useMousePopover } from '../hooks/useMousePopover'
import { TreeMenu } from '../menus/TreeMenu'
import { EnvironmentPanel, SchemaPinboardPanel, SchemaSearchPanel } from '../panels'
import { ItemBreadcrumb } from './components'
import { SchemaInspector, SchemaWorksheet, TreeNavigator } from './schema'
import { WorkspaceView } from './WorkspaceView'

export const SchemaWorkspace = observer(function SchemaWorkspace({
    user,
    project,
    viewkey,
    grow,
    ...props
}) {
    const { app, view } = useStore()
    const treeviewHandle = useRef(null)

    const sep = (
        <span key="_" className="breadcrumbs-separator">
            {app.text('›')}
        </span>
    )

    const tree = view.schematree
    const worksheet = view.schemaworksheet
    const schemaquery = view.schemaquery

    const [pinboardVisible, setPinboardVisible] = usePersistentState(
        viewkey + '/pinboardVisible',
        true
    )

    const [navigatorTab, setNavigatorTab] = usePersistentState(
        viewkey + '/navigatorTab',
        'tree'
    )

    const TreeContextMenu = useMousePopover(TreeMenu)

    let NavigationToolbar
    NavigationToolbar = (
        <>
            <TreeContextMenu.Panel
                onAction={action => {
                    if (action === 'focus_root') {
                        view._schematree.collapseAll()
                    } else if (action === 'focus_selected') {
                        view._schematree.collapseToSelected().then(() => {
                            if (worksheet.item) {
                                scrollIntoView(worksheet.datatype, worksheet.item.gid)
                            }
                        })
                    }

                    TreeContextMenu.hide()
                }}
            />
            <Button
                icon
                active={navigatorTab === 'tree' ? true : false}
                onClick={() => setNavigatorTab('tree')}
                title={app.text('Tree')}
                onContextMenu={e => {
                    if (navigatorTab === 'tree') {
                        TreeContextMenu.onShow(e)
                    }
                }}
            >
                <Icon name="tree" size={1} />
            </Button>
            <Button
                icon
                active={navigatorTab === 'search' ? true : false}
                onClick={() => setNavigatorTab('search')}
                title={app.text('Search')}
            >
                <Icon name="search" size={1} />
            </Button>
        </>
    )

    const onSelect = itemgid => {
        const selectedItem = worksheet.getItem(itemgid)
        if (selectedItem) {
            worksheet.setItem(selectedItem)
        }
    }

    let Navigator
    switch (navigatorTab) {
        case 'tree':
            Navigator = (
                <TreeNavigator tree={tree} worksheet={worksheet} ref={treeviewHandle} />
            )
            break
        case 'search':
            Navigator = (
                <SchemaSearchPanel
                    querystore={schemaquery}
                    preview={false}
                    showMatches={true}
                    multiselect={true}
                    onSelect={onSelect}
                    selectedSchemaGid={worksheet.item ? worksheet.item.gid : null}
                    style={{ marginTop: 5 }}
                />
            )
            break
        default:
            Navigator = <VView grow />
            break
    }

    function getScrollOffsets(dataType, itemGid, groupItemHeight, schemaItemHeight) {
        let scrollTop = 0
        let scrollTopDatatype = 0
        let scrollTopItem = 0
        for (const treeItem of view._schematree.tree._treeitems) {
            if (!treeItem.id) continue
            if (treeItem.id === itemGid) {
                view._schematree.tree.selectItem(treeItem)
                scrollTopItem = scrollTop
            }
            if (treeItem.id === dataType) {
                scrollTopDatatype = scrollTop
            }
            if (['definition', 'class', 'field'].includes(treeItem.id)) {
                scrollTop += groupItemHeight
            } else {
                scrollTop += schemaItemHeight
            }
        }

        return { scrollTopDatatype, scrollTopItem }
    }

    function scrollTo({ scrollTopDatatype, scrollTopItem }) {
        if (treeviewHandle.current) {
            // already in view
            if (
                scrollTopItem &&
                scrollTopItem >= treeviewHandle.current.scrollTop &&
                scrollTopItem <=
                    treeviewHandle.current.scrollTop +
                        treeviewHandle.current.offsetHeight -
                        treeschemaitemheight
            ) {
                return
            } else if (
                !scrollTopItem &&
                scrollTopDatatype >= treeviewHandle.current.scrollTop &&
                scrollTopDatatype <=
                    treeviewHandle.current.scrollTop +
                        treeviewHandle.current.offsetHeight -
                        treegroupitemheight
            ) {
                return
            }
            // can we show the group?
            let newScrollTop
            const typeItemDiff = scrollTopItem ? scrollTopItem - scrollTopDatatype : 0
            if (
                treeviewHandle.current.offsetHeight >
                typeItemDiff + treeschemaitemheight
            ) {
                // show the group at the top
                newScrollTop = scrollTopDatatype
            } else {
                // this the item a bit above the middle
                newScrollTop = scrollTopItem - treeviewHandle.current.offsetHeight * 0.4
            }
            treeviewHandle.current.scrollTop = Math.max(0, newScrollTop)
        }
    }

    const scrollIntoView = (dataType, itemGid) => {
        // make sure path to itemGid is open (keep the existing expandedIds too)
        const treeIds =
            dataType === 'definition'
                ? view._schematree.getTreeIdsForDefinitionGid(itemGid)
                : []
        const parentIds = [
            ...new Set([
                ...treeIds, // make sure definition tree is open where/if needed
                ...view._schematree.tree._expandedIds, // and all the current ids
                ...(itemGid ? [dataType] : []), // and the root for the item type
            ]),
        ]
        return view._schematree.fetch(parentIds).then(() => {
            // then determine offset
            // and scroll there
            const { scrollTopDatatype, scrollTopItem } = getScrollOffsets(
                dataType,
                itemGid,
                treegroupitemheight,
                treeschemaitemheight
            )
            scrollTo({ scrollTopDatatype, scrollTopItem })
        })
    }

    let WorksheetToolbar
    const treegroupitemheight = 19
    const treeschemaitemheight = 34
    if (worksheet.item) {
        WorksheetToolbar = (
            <>
                <span onClick={() => scrollIntoView(worksheet.datatype)}>
                    {worksheet.datatypeTexts[worksheet.datatype]}
                </span>
                {sep}
                <span
                    onClick={() =>
                        scrollIntoView(worksheet.datatype, worksheet.item.gid)
                    }
                >
                    <ItemBreadcrumb item={worksheet.item} />
                </span>
            </>
        )
    }

    let InspectorToolbar = <span>{app.text('Actions')}</span>
    if (worksheet.item) {
        InspectorToolbar = <ItemBreadcrumb item={worksheet.item} ellipsisAfter={32} />
    }

    const [
        { isOver: isOverPinboardToggle, canDrop: canDropPinboardToggle },
        dropPinboardToggle,
    ] = useDrop({
        accept: [DragTypes.PIM_DEFINITION, DragTypes.PIM_CLASS, DragTypes.PIM_FIELD],
        canDrop: () => view.schemapinboard.canAdd(),
        collect: monitor => {
            return {
                isOver: !!monitor.isOver(),
                canDrop: !!monitor.canDrop(),
            }
        },
        drop: ({ id }) => {
            view.schemapinboard.add(id)
            setPinboardVisible(true)
            return { receiver: 'PinboardToggle' }
        },
    })

    const Pinboard = pinboardVisible ? (
        <>
            <Divider />
            <SchemaPinboardPanel
                pinboard={view.schemapinboard}
                selectedItem={worksheet.item}
            />
        </>
    ) : undefined

    let classesPinboardToggle = 'pinboardtoggle-drop'
    if (isOverPinboardToggle) {
        classesPinboardToggle += ' drag-over'
        if (!canDropPinboardToggle) classesPinboardToggle += ' drop-rejected'
    }
    if (!pinboardVisible && !view.schemapinboard.canAdd()) {
        classesPinboardToggle += ' pinboard-full'
    }

    const PinboardToggle = (
        <div className={classesPinboardToggle} ref={dropPinboardToggle}>
            <Button
                icon
                active={pinboardVisible}
                title={app.text(
                    pinboardVisible
                        ? 'Hide schema pinboard panel'
                        : 'Show schema pinboard panel'
                )}
                onClick={() => setPinboardVisible(!pinboardVisible)}
            >
                <Icon name="schema" size={1} />
                <Icon name="pin" size={1} />
            </Button>
        </div>
    )

    const NavigatorView = (
        <>
            <HView className="toolbar">{NavigationToolbar}</HView>
            <Divider />
            {Navigator}
            {Pinboard}
        </>
    )
    const WorksheetView = (
        <>
            <HView className="toolbar">{WorksheetToolbar}</HView>
            <Divider />
            <VScrollView>
                <SchemaWorksheet worksheet={worksheet} />
            </VScrollView>
        </>
    )
    const InspectorView = (
        <>
            <div className="toolbar">{InspectorToolbar}</div>
            <Divider />
            <VScrollView>
                <SchemaInspector tree={tree} worksheet={worksheet} viewkey={viewkey} />
            </VScrollView>
        </>
    )
    const StatusbarView = (
        <>
            {PinboardToggle}
            <Spacer size={15} />
            <EnvironmentPanel
                project={project}
                environment={view.environment}
                updateEnvironment={view.updateEnvironment}
                showLanguage
                showLabelView
            />
        </>
    )

    return (
        <WorkspaceView
            grow={grow}
            className="workspace-schema"
            viewkey={viewkey}
            navigator={NavigatorView}
            worksheet={WorksheetView}
            inspector={InspectorView}
            statusbar={StatusbarView}
        />
    )
})
